import {useCallback, useEffect, useRef} from 'react';
import type {ApolloClient} from '@apollo/client';
import {useApolloClient, useSubscription} from '@apollo/client/react';

import {graphql} from '../gqlApp';

/**
 * Общие realtime-подписки (SKS-249, Tier 0/1).
 *
 * `warehouseActivity` — сигнал «на складе изменилось» для любой складской доски;
 * `companyActivity` — «в делах компании изменилось» для списков компании. Оба —
 * сигнал+refetch: подписка будит экран, он перезапрашивает свой запрос. Источник —
 * журнал событий на бэке, поэтому один сигнал обслуживает много досок/списков.
 */

export const WAREHOUSE_ACTIVITY = graphql(`
	subscription WarehouseActivity($warehouseId: ID!) {
		warehouseActivity(warehouseId: $warehouseId) {
			warehouseId
		}
	}
`);

export const COMPANY_ACTIVITY = graphql(`
	subscription CompanyActivity($companyId: ID!) {
		companyActivity(companyId: $companyId) {
			companyId
		}
	}
`);

/** Пауза схлопывания всплеска сигналов в один перезапрос. */
const REFETCH_DEBOUNCE_MS = 400;

/**
 * Дебаунс перезапроса по сигналу (SKS-249). Один сигнал = один refetch был бы шумным:
 * burst-домены (напр. импорт каталога пишет пачку событий с sellerCompanyId) дёргали бы
 * подписанные списки на каждое событие. Схлопываем всплеск в один перезапрос.
 */
export const useDebouncedRefetch = (queries: string[]): ((client: ApolloClient) => void) => {
	const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
	const key = queries.join(',');

	useEffect(() => () => clearTimeout(timer.current), []);

	return useCallback(
		(client: ApolloClient) => {
			clearTimeout(timer.current);
			timer.current = setTimeout(() => {
				void client.refetchQueries({include: key.split(',')});
			}, REFETCH_DEBOUNCE_MS);
		},
		[key],
	);
};

/** Подписка на активность склада → перезапрос указанных запросов доски (с дебаунсом). */
export const useWarehouseActivityRefetch = (
	warehouseId: string | undefined,
	queries: string[],
) => {
	const refetch = useDebouncedRefetch(queries);

	useSubscription(WAREHOUSE_ACTIVITY, {
		variables: {warehouseId: warehouseId ?? ''},
		skip: !warehouseId,
		onData: ({client}) => refetch(client),
	});
};

/** Подписка на активность компании → перезапрос указанных запросов-списков (с дебаунсом). */
export const useCompanyActivityRefetch = (companyId: string | undefined, queries: string[]) => {
	const refetch = useDebouncedRefetch(queries);

	useSubscription(COMPANY_ACTIVITY, {
		variables: {companyId: companyId ?? ''},
		skip: !companyId,
		onData: ({client}) => refetch(client),
	});
};

/**
 * Подписка на активность нескольких складов → один перезапрос с общим дебаунсом.
 *
 * Списку «все склады» (SKS-622) сигналов нужно столько же, сколько складов: общего
 * «компания изменилась» у складской стороны нет — companyActivity адресован селлеру,
 * а складские события несут warehouseId. useSubscription здесь не годится: хуков
 * нельзя иметь переменное число, поэтому подписки открываются вручную на весь набор.
 */
export const useWarehousesActivityRefetch = (
	warehouseIds: readonly string[],
	queries: string[],
) => {
	const client = useApolloClient();
	const refetch = useDebouncedRefetch(queries);

	// Ключ вместо массива в зависимостях: родитель собирает список складов заново на
	// каждый рендер, а пересматривать подписки нужно только при смене набора
	const key = warehouseIds.join(',');

	useEffect(() => {
		if (!key) {
			return;
		}

		const subscriptions = key
			.split(',')
			.map((warehouseId) =>
				client.subscribe({query: WAREHOUSE_ACTIVITY, variables: {warehouseId}}).subscribe({
					next: () => refetch(client),
					/*
					 * Ошибку канала глотаем: переподключение ws-link делает сам, а отказ
					 * подписки — не повод ронять экран «все склады» в error boundary.
					 */
					error: () => undefined,
				}),
			);

		return () => {
			for (const subscription of subscriptions) {
				subscription.unsubscribe();
			}
		};
	}, [client, key, refetch]);
};
